
import Vue from "vue";
import { add_candidates_with_resume, get_candidate_pools } from "@/store/apis/recruiter";
import AIAgentLoader from "@/components/shared/AIAgentLoader.vue";

interface UploadFile {
  file: File;
  name: string;
  type: string;
  size: number;
  progress: number;
  status: 'pending' | 'uploading' | 'completed' | 'error' | 'success' | 'duplicate';
}

interface ExistingPool {
  id: string;
  name: string;
  candidateCount: number;
}

export default Vue.extend({
  name: "UploadCVsModal",
  components: {
    AIAgentLoader
  },
  props: {
    value: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      dialog: false,
      isDragOver: false,
      uploadedFiles: [] as UploadFile[],
      isUploading: false,
      poolName: '',
      poolMode: 'new' as 'new' | 'existing',
      selectedPoolId: '',
      existingPools: [] as ExistingPool[],
      loadingPools: false,
      overallProgress: 0,
      poolNameRules: [
        (value: string) => !!value || this.$t('recruiter.candidates.view.pool-name-required')
      ]
    };
  },
  computed: {
    canUpload(): boolean {
      const hasValidPool = this.poolMode === 'new' 
        ? !!this.poolName.trim() 
        : !!this.selectedPoolId;
      return hasValidPool && this.uploadedFiles.length > 0 && !this.isUploading;
    },
    finalPoolName(): string {
      if (this.poolMode === 'new') {
        return this.poolName.trim();
      } else {
        const pool = this.existingPools.find(p => p.id === this.selectedPoolId);
        return pool?.name || '';
      }
    }
  },
  watch: {
    value(newVal) {
      this.dialog = newVal;
    },
    dialog(newVal) {
      this.$emit('input', newVal);
      
      // Load pools when modal opens
      if (newVal) {
        this.loadExistingPools();
      }
    }
  },
  mounted() {
    this.loadExistingPools();
  },
  methods: {
    async loadExistingPools() {
      this.loadingPools = true;
      try {
        const response = await get_candidate_pools();
        
        // Check for success using statusCode (200) and data array
        if (response.data && response.data.statusCode === 200 && response.data.data) {
          // Transform API response to match our interface
          this.existingPools = response.data.data.map((pool: any) => ({
            id: pool.id.toString(), // Convert to string for consistency
            name: pool.pool_name,
            candidateCount: pool.candidate_count || 0
          }));
        } else {
          // Fallback to empty array if API fails
          this.existingPools = [];
        }
      } catch (error) {
        console.error('Error loading existing pools:', error);
        // Fallback to empty array on error
        this.existingPools = [];
      } finally {
        this.loadingPools = false;
      }
    },
    
    setPoolMode(mode: 'new' | 'existing') {
      this.poolMode = mode;
      // Clear selections when switching modes
      this.poolName = '';
      this.selectedPoolId = '';
      
      // Don't call API here - pools are already loaded on component mount
    },
    
    onPoolSelect(value: string) {
      this.selectedPoolId = value;
    },
    
    closeModal() {
      // Prevent closing modal during upload process
      if (this.isUploading) {
        return;
      }
      
      this.dialog = false;
      this.resetData();
    },
    
    resetData() {
      this.uploadedFiles = [];
      this.isDragOver = false;
      this.isUploading = false;
      this.poolName = '';
      this.poolMode = 'new';
      this.selectedPoolId = '';
      this.overallProgress = 0;
    },
    
    clearAllFiles() {
      this.uploadedFiles = [];
    },
    triggerFileInput() {
      (this.$refs.fileInput as HTMLInputElement).click();
    },
    handleFileSelect(event: Event) {
      const target = event.target as HTMLInputElement;
      if (target.files) {
        this.processFiles(Array.from(target.files));
      }
    },
    handleDrop(event: DragEvent) {
      event.preventDefault();
      this.isDragOver = false;
      
      if (event.dataTransfer?.files) {
        this.processFiles(Array.from(event.dataTransfer.files));
      }
    },
    handleDragOver(event: DragEvent) {
      event.preventDefault();
      this.isDragOver = true;
    },
    handleDragLeave(event: DragEvent) {
      event.preventDefault();
      this.isDragOver = false;
    },
    processFiles(files: File[]) {
      const validFiles = files.filter(file => {
        const isValidType = file.type === 'application/pdf' || 
                           file.type === 'application/msword' ||
                           file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
                           file.type === 'text/plain';
        const isValidSize = file.size <= 2 * 1024 * 1024; // 2MB limit
        
        if (!isValidType) {
          this.$emit('error', 'Unsupported file type. Only PDF, DOC, DOCX, and TXT files are allowed.');
          return false;
        }
        
        if (!isValidSize) {
          this.$emit('error', 'File size should be less than 2 MB!');
          return false;
        }
        
        return true;
      });

      validFiles.forEach(file => {
        const uploadFile: UploadFile = {
          file,
          name: file.name,
          type: file.type.split('/')[1],
          size: file.size,
          progress: 0,
          status: 'pending'
        };
        this.uploadedFiles.push(uploadFile);
      });
    },
    removeFile(index: number) {
      this.uploadedFiles.splice(index, 1);
    },
    formatFileSize(bytes: number): string {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },
    getFileIcon(fileType: string): string {
      switch (fileType) {
        case 'pdf':
          return 'mdi-file-pdf-box';
        case 'doc':
        case 'docx':
          return 'mdi-file-word-box';
        case 'txt':
          return 'mdi-file-document-outline';
        default:
          return 'mdi-file-document-outline';
      }
    },
    getFileIconColor(fileType: string): string {
      switch (fileType) {
        case 'pdf':
          return 'red';
        case 'doc':
        case 'docx':
          return 'blue';
        case 'txt':
          return 'green';
        default:
          return 'grey';
      }
    },
    getFileTypeDisplay(fileType: string): string {
      switch (fileType) {
        case 'pdf':
          return 'PDF';
        case 'doc':
          return 'DOC';
        case 'docx':
          return 'DOCX';
        case 'txt':
          return 'TXT';
        default:
          return fileType.toUpperCase();
      }
    },
    async uploadFiles() {
      if (this.uploadedFiles.length === 0) return;
      
      // Validate pool selection
      if (!this.finalPoolName) {
        this.$emit('error', this.$t('recruiter.candidates.view.pool-name-required'));
        return;
      }
      
      this.isUploading = true;
      
      try {
        // Prepare FormData for multipart/form-data
        const formData = new FormData();
        
        // Add pool name - backend will handle pool creation/management automatically
        formData.append('pool_name', this.finalPoolName);
        
        // Add all files with field name 'candidate_resume'
        this.uploadedFiles.forEach(file => {
          formData.append('candidate_resume', file.file);
          file.status = 'uploading';
          file.progress = 0;
        });

        // Make API call with progress tracking
        const response = await add_candidates_with_resume(formData, (progressEvent: any) => {
          const totalProgress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          this.overallProgress = totalProgress;
          
          // Distribute progress evenly among all files
          this.uploadedFiles.forEach(file => {
            file.progress = totalProgress;
          });
        });

        // Handle response
        console.log('📤 Upload response:', response.data);
        
        if (response.data.statusCode === 200) {
          const { added, duplicates, failed } = response.data.data;
          
          console.log('✅ Upload successful:', { added, duplicates, failed });
          
          // Update file statuses based on response
          this.uploadedFiles.forEach(file => {
            const fileName = file.name;
            
            if (added.find((item: any) => item.file === fileName)) {
              file.status = 'completed';
              file.progress = 100;
            } else if (duplicates.find((item: any) => item.file === fileName)) {
              file.status = 'error';
              file.progress = 100;
            } else if (failed.find((item: any) => item.file === fileName)) {
              file.status = 'error';
              file.progress = 100;
            }
          });
          
          this.overallProgress = 100;
          
          // Prepare success message
          let message = `Processing completed! `;
          if (added.length > 0) message += `${added.length} files added successfully to pool "${this.finalPoolName}". `;
          if (duplicates.length > 0) message += `${duplicates.length} duplicates found. `;
          if (failed.length > 0) message += `${failed.length} files failed.`;
          
          console.log('📢 Emitting success event:', message);
          
          // Emit success event - parent will handle closing and refreshing
          this.$emit('success', message);
          
          console.log('🚪 Closing modal...');
          
          // Set uploading to false first, then close modal
          this.isUploading = false;
          
          // Close modal immediately after emitting success
          // Parent component will handle data refresh
          this.closeModal();
        } else {
          console.error('❌ Upload failed:', response.data);
          throw new Error(response.data.message || 'Upload failed');
        }
        
      } catch (error: any) {
        console.error('Upload error:', error);
        
        // Update all files to error status
        this.uploadedFiles.forEach(file => {
          file.status = 'error';
          file.progress = 100;
        });
        
        const errorMessage = error.response?.data?.message || error.message || 'Upload failed. Please try again.';
        this.$emit('error', errorMessage);
        
        // Don't auto-close on error - let user see what went wrong and retry
      } finally {
        this.isUploading = false;
      }
    },
    
    getProgressColor(status: string): string {
      switch (status) {
        case 'uploading':
          return 'primary';
        case 'completed':
        case 'success':
          return 'success';
        case 'error':
          return 'error';
        case 'duplicate':
          return 'warning';
        default:
          return 'primary';
      }
    },
    
    getStatusClass(status: string): string {
      switch (status) {
        case 'uploading':
          return 'status-processing';
        case 'completed':
        case 'success':
          return 'status-success';
        case 'error':
          return 'status-error';
        case 'duplicate':
          return 'status-warning';
        default:
          return 'status-pending';
      }
    },
    
    getStatusText(status: string): string {
      switch (status) {
        case 'pending':
          return 'Pending';
        case 'uploading':
          return 'Uploading...';
        case 'completed':
        case 'success':
          return 'Completed';
        case 'error':
          return 'Error';
        case 'duplicate':
          return 'Duplicate';
        default:
          return 'Unknown';
      }
    }
  }
});
